/**
 * React context exposing the Flow Designer's canvas column as a portal host.
 *
 * Slide-up overlays (workflow settings, playbook, scenario config) are rendered
 * as direct children of the canvas column and position themselves with
 * `absolute inset-0`, so they cover the canvas but not the right sidebar or the
 * top nav. Overlays that are *mounted* from inside the sidebar — the API
 * Sequence editor reached from a node's Configure tab — cannot do that: the
 * sidebar root is `relative` (it positions the resize handle), so it becomes
 * their containing block and clips them to ~380px.
 *
 * This context hands those overlays the canvas column element so they can
 * `createPortal` into it and keep the same `absolute inset-0` markup.
 *
 * @module FlowDesigner/context/FlowOverlayPortalContext
 */

import { createContext, useContext } from 'react';

const FlowOverlayPortalContext = createContext<HTMLElement | null>(null);

export const FlowOverlayPortalProvider = FlowOverlayPortalContext.Provider;

/**
 * The canvas-column element to portal flow overlays into, or `null` when
 * rendered outside a Flow Designer (tests, standalone usage) — callers should
 * fall back to rendering in place.
 */
export function useFlowOverlayPortalHost(): HTMLElement | null {
  return useContext(FlowOverlayPortalContext);
}
